import { useState, useEffect, useRef, type ChangeEvent, type DragEvent } from "react";
import { assetService } from "../../services/assetService";
import {
    type Asset,
    assetFilename,
    assetVariant,
    defaultAsset,
    groupAssetsByFilename,
    previewAsset,
} from "../../types/asset";
import { CollapsibleSection } from "../../components/CollapsibleSection";

interface AssetListProps {
    blogId: string | number;
    isOpen?: boolean;
    onToggle?: () => void;
    refreshKey?: number;
    onInsert?: (markdown: string) => void;
}

function toAssetMarkdown(asset: Asset): string {
    const label = assetFilename(asset).replace(/[[\]]/g, "");
    if (!isImageAsset(asset)) {
        return `[${label}](${asset.url})`;
    }
    const alt = label.replace(/\.[^.]+$/, "");
    return `![${alt}](${asset.url})`;
}

function isImageAsset(asset: Asset) {
    return asset.content_type?.startsWith("image/");
}

function imageFilesFromList(files: FileList | File[]) {
    return Array.from(files).filter((file) => file.type.startsWith("image/"));
}

export function AssetList({ blogId, isOpen, onToggle, refreshKey, onInsert }: AssetListProps) {
    const [assets, setAssets] = useState<Asset[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [actionFeedback, setActionFeedback] = useState<{ id: number; type: "copied" | "inserted" } | null>(null);
    const [selectedByFile, setSelectedByFile] = useState<Record<string, number>>({});
    const dragDepth = useRef(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const groups = groupAssetsByFilename(assets);

    const fetchAssets = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const next = await assetService.list("blog", blogId);
            setAssets(next);
        } catch (err) {
            console.error("Failed to fetch assets", err);
            setError("Failed to load assets");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (blogId) {
            fetchAssets();
        }
    }, [blogId, refreshKey]);

    useEffect(() => {
        return () => {
            if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
        };
    }, []);

    const showFeedback = (id: number, type: "copied" | "inserted") => {
        setActionFeedback({ id, type });
        if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
        feedbackTimer.current = setTimeout(() => setActionFeedback(null), 1200);
    };

    const uploadFiles = async (files: FileList | File[]) => {
        const images = imageFilesFromList(files);
        if (images.length === 0 || !blogId) return;

        setIsUploading(true);
        setError(null);
        try {
            for (const file of images) {
                await assetService.upload("blog", blogId, file);
            }
            await fetchAssets();
        } catch (err) {
            console.error("Failed to upload asset", err);
            setError("Failed to upload asset");
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileInput = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files?.length) return;
        await uploadFiles(files);
        e.target.value = "";
    };

    const selectedInGroup = (filename: string, variants: Asset[]) => {
        const selectedId = selectedByFile[filename];
        return variants.find((asset) => asset.id === selectedId) ?? defaultAsset(variants);
    };

    const handleCopy = async (asset: Asset) => {
        try {
            await navigator.clipboard.writeText(toAssetMarkdown(asset));
            showFeedback(asset.id, "copied");
        } catch (err) {
            console.error("Failed to copy asset markdown", err);
        }
    };

    const handleInsert = (asset: Asset) => {
        onInsert?.(toAssetMarkdown(asset));
        showFeedback(asset.id, "inserted");
    };

    const handleDragEnter = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepth.current += 1;
        if (e.dataTransfer.types.includes("Files")) {
            setIsDraggingOver(true);
        }
    };

    const handleDragOver = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "copy";
    };

    const handleDragLeave = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) {
            dragDepth.current = 0;
            setIsDraggingOver(false);
        }
    };

    const handleDrop = async (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepth.current = 0;
        setIsDraggingOver(false);
        await uploadFiles(e.dataTransfer.files);
    };

    return (
        <CollapsibleSection
            title="Assets"
            isOpen={isOpen}
            onToggle={onToggle}
        >
            <div
                className={`relative flex flex-col gap-3 border-t border-base-content/5 pt-3 min-h-0 rounded-lg ${isDraggingOver ? "ring-2 ring-primary/60 ring-inset bg-primary/5" : ""}`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileInput}
                    disabled={isUploading}
                    accept="image/*"
                    multiple
                />

                <div className="flex justify-between items-center">
                    <span className="text-xs opacity-40 italic">
                        {groups.length} {groups.length === 1 ? "file" : "files"}
                    </span>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm gap-1"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                    >
                        {isUploading ? (
                            <span className="loading loading-spinner loading-xs"></span>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-4 h-4 stroke-current">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                            </svg>
                        )}
                        {isUploading ? "Uploading..." : "Upload"}
                    </button>
                </div>

                {isLoading && assets.length === 0 ? (
                    <div className="flex justify-center py-8">
                        <span className="loading loading-spinner loading-sm text-primary"></span>
                    </div>
                ) : error ? (
                    <div className="text-error text-xs text-center py-2">{error}</div>
                ) : groups.length === 0 ? (
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className="flex flex-col items-center justify-center gap-2 py-10 px-4 rounded-xl border border-dashed border-base-content/20 text-sm opacity-60 hover:opacity-100 hover:border-primary/40 hover:bg-base-200/40 transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-8 h-8 stroke-current opacity-70">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                        </svg>
                        Drop images here or click to upload
                    </button>
                ) : (
                    <div className="grid grid-cols-2 gap-2">
                        {groups.map(({ filename, variants }) => {
                            const selected = selectedInGroup(filename, variants);
                            const preview = previewAsset(variants);
                            const feedback = actionFeedback?.id === selected.id ? actionFeedback.type : null;

                            return (
                                <div
                                    key={filename}
                                    className="overflow-hidden rounded-xl bg-base-200 border border-base-content/5"
                                >
                                    <div className="aspect-square bg-base-300">
                                        {isImageAsset(preview) ? (
                                            <img
                                                src={preview.url}
                                                alt={filename}
                                                className="h-full w-full object-cover"
                                            />
                                        ) : (
                                            <div className="flex h-full w-full items-center justify-center text-xs opacity-50 px-2 text-center">
                                                {filename}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1.5 p-1.5">
                                        <span className="min-w-0 truncate px-0.5 text-[11px] font-medium" title={filename}>
                                            {feedback === "copied" ? "Copied" : feedback === "inserted" ? "Inserted" : filename}
                                        </span>
                                        <div className="flex flex-wrap gap-0.5">
                                            {variants.map((asset) => {
                                                const variant = assetVariant(asset);
                                                const isSelected = asset.id === selected.id;
                                                return (
                                                    <button
                                                        key={asset.id}
                                                        type="button"
                                                        className={`badge badge-xs border-0 ${isSelected ? "badge-primary" : "badge-ghost opacity-70"}`}
                                                        onClick={() =>
                                                            setSelectedByFile((current) => ({
                                                                ...current,
                                                                [filename]: asset.id,
                                                            }))
                                                        }
                                                    >
                                                        {variant}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div className="flex gap-1">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs flex-1"
                                                onClick={() => handleCopy(selected)}
                                            >
                                                Copy
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs flex-1"
                                                onClick={() => handleInsert(selected)}
                                            >
                                                Insert
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {isDraggingOver && (
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-base-100/80 text-sm font-medium">
                        Drop to upload
                    </div>
                )}
            </div>
        </CollapsibleSection>
    );
}
